// «Saltar a…» (rebrand 03-11, como Spotlight; maqueta web/qa/rebrand-apple/despues/5-saltar.png). Pau cambia de agente en
// el 64 % de sus mensajes seguidos (analisis/uso-oficina): Ctrl/⌘+K abre un buscador; escribes tres letras y Intro. Sin
// escribir nada, el primero es el que más lleva esperándote (el orden de la lista), así que Ctrl+K, Intro = «el siguiente
// que me necesita». También acciones: contratar (con lo escrito como encargo) y abrir la Sala de Control.
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Activity, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { useOficinaStore } from '../store';
import { IconoEncargo } from '../ui';
import { quien } from './estado';
import { aspectoDe, etiquetaSituacion, seVa } from './situacion';
import { useEquipo } from './useEquipo';

/** Otra pieza (el botón de la lista) pide abrirlo con este evento. */
export const EVENTO_SALTAR = 'oficina:saltar';
export const abrirSaltar = () => window.dispatchEvent(new Event(EVENTO_SALTAR));

/** Sin mayúsculas ni tildes: «publi» encuentra «Publicar», «catalan» encuentra «catalán». */
export function normalizar(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

type Opcion = { tipo: 'agente'; c: ContratadoDTO } | { tipo: 'contratar' } | { tipo: 'sala' };

export function Saltar({ contratados, alAbrir, alSala }: { contratados: Record<string, ContratadoDTO>; alAbrir: (agentId: string) => void; alSala: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [sel, setSel] = useState(0);
  const { orden, necesita } = useEquipo(contratados);
  const chatAbierto = useOficinaStore((s) => s.chatAbierto);
  const abrirContratar = useOficinaStore((s) => s.abrirContratar);

  useEffect(() => {
    const abrir = () => {
      setTexto('');
      setSel(0);
      setAbierto(true);
    };
    const tecla = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        abrir();
      }
    };
    window.addEventListener('keydown', tecla);
    window.addEventListener(EVENTO_SALTAR, abrir);
    return () => {
      window.removeEventListener('keydown', tecla);
      window.removeEventListener(EVENTO_SALTAR, abrir);
    };
  }, []);

  const opciones = useMemo<Opcion[]>(() => {
    const q = normalizar(texto.trim());
    const vivos = orden.filter((c) => !seVa(c));
    // Sin escribir, el que ya tienes abierto no se ofrece: Ctrl+K, Intro = «el siguiente que me necesita», no el mismo.
    const agentes = q ? vivos.filter((c) => normalizar(`${quien(c)} ${c.nombre}`).includes(q)) : vivos.filter((c) => c.agentId !== chatAbierto);
    return [...agentes.slice(0, 8).map((c) => ({ tipo: 'agente' as const, c })), { tipo: 'contratar' }, { tipo: 'sala' }];
  }, [orden, texto, chatAbierto]);

  const abrioChat = useRef(false);
  const elegir = (o: Opcion | undefined) => {
    if (!o) return;
    abrioChat.current = o.tipo === 'agente';
    setAbierto(false);
    if (o.tipo === 'agente') alAbrir(o.c.agentId);
    else if (o.tipo === 'contratar') abrirContratar(undefined, texto.trim() || undefined);
    else alSala();
  };

  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={setAbierto}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-label={t('oficinaV2.saltar.titulo')}
          // Si se abrió un chat, al cerrarse no devuelve el foco a lo de antes: el cursor se queda en la caja del chat.
          onCloseAutoFocus={(e) => {
            if (abrioChat.current) e.preventDefault();
            abrioChat.current = false;
          }}
          className="fixed left-1/2 top-[14vh] z-[61] flex max-h-[70vh] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-o-xl bg-oficina-elevada text-oficina-texto shadow-o-hoja outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title className="sr-only">{t('oficinaV2.saltar.titulo')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{t('oficinaV2.saltar.ayuda')}</DialogPrimitive.Description>
          <label className="flex items-center gap-3 border-b border-oficina-linea px-4 py-3.5">
            <Search className="h-5 w-5 shrink-0 text-oficina-texto2" aria-hidden />
            <input
              autoFocus
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setSel(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSel((s) => Math.min(s + 1, opciones.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSel((s) => Math.max(s - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  elegir(opciones[sel]);
                }
              }}
              placeholder={t('oficinaV2.saltar.placeholder')}
              aria-label={t('oficinaV2.saltar.placeholder')}
              aria-activedescendant={`saltar-${sel}`}
              aria-controls="saltar-lista"
              className="min-w-0 flex-1 bg-transparent text-o-titulo3 text-oficina-texto outline-none placeholder:text-oficina-texto3"
            />
            <kbd className="rounded-o-s border border-oficina-linea px-1.5 font-sans text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{t('oficinaV2.saltar.esc')}</kbd>
          </label>
          <ul id="saltar-lista" role="listbox" aria-label={t('oficinaV2.saltar.titulo')} className="min-h-0 overflow-y-auto p-2">
            {opciones.map((o, i) => (
              <li
                key={o.tipo === 'agente' ? o.c.agentId : o.tipo}
                id={`saltar-${i}`}
                role="option"
                aria-selected={i === sel}
                onMouseMove={() => setSel(i)}
                onClick={() => elegir(o)}
                className={cn('flex cursor-pointer items-center gap-3 rounded-o-m px-2.5 py-2', i === sel && 'bg-oficina-pulsado')}
                data-testid="saltar-opcion"
              >
                {o.tipo === 'agente' ? <FilaAgente c={o.c} toca={necesita(o.c)} /> : o.tipo === 'contratar' ? (
                  <>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-o-s bg-oficina-control text-oficina-acento" aria-hidden>
                      <Plus className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-o-texto font-medium">{texto.trim() ? t('oficinaV2.saltar.contratarPara', { texto: texto.trim() }) : t('oficinaV2.saltar.contratar')}</span>
                  </>
                ) : (
                  <>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-o-s bg-oficina-error text-white" aria-hidden>
                      <Activity className="h-4 w-4" strokeWidth={2.4} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-o-texto font-medium">{t('oficinaV2.saltar.sala')}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
          {/* Las teclas solo sirven con teclado: con el dedo no se enseñan. */}
          <p className="flex gap-4 border-t border-oficina-linea px-4 py-2 text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{t('oficinaV2.saltar.ayuda')}</p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FilaAgente({ c, toca }: { c: ContratadoDTO; toca: boolean }) {
  return (
    <>
      <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={32} />
      <span className={cn('min-w-0 flex-1 truncate text-o-texto', toca ? 'font-semibold' : 'font-medium')}>{quien(c)}</span>
      <span className={cn('shrink-0 text-o-sub', toca ? aspectoDe(c).texto : 'text-oficina-texto3')}>{etiquetaSituacion(c.situacion)}</span>
    </>
  );
}
